import React from 'react';
import { Link } from "react-router-dom";
import routes from 'src/routes';
import Page from 'src/components/Page';
import Table from 'src/components/Table';
import SpecLink from 'src/components/SpecLink';
import CodeSamples from 'src/components/CodeSamples';

function TxnAuthenticationIframePage() {

    return <Page header="Integrate authentication">
        <p>
            Authentication (3D Secure) can be integrated into any payment methods that supports <Link to={routes.api.txn.authkey.gettingStarted}>AuthKey</Link>.
        </p>

        <h2>How it works</h2>
        <p>
            3D Secure will provide the Issuing Bank (the card holder's financial institution) with details about the payment.  This could include the device the payment is being made from,
            the amount and payment details.  The Issuing Bank will analyse the details and do one of the following:            
        </p>

        <ul>
            <li>Allow the payment to be processed, or</li>
            <li>Issue a challenge which the customer on your site must complete in order for the transaction to be processed.</li>
       </ul>

       <p>
            The challenge could be a code or password that only the Issuing Bank or the Customer know, hence why the Challenge is hosted in an iFrame and not accessible to the merchant.
       </p>

        <h2>Notes on implementation</h2>
        <p>
            Each merchant has their own unique payment implementation.  The steps below identify how to implement 3D Secure with all the features.  For example, the steps below
            guide you on how to implement the iFrame within the same page as the payment details to demonstrate how showing and hiding the iFrame can work.  You may wish to 
            have a pop-up present the Challenge, ignoring hiding and showing events.  Please use the instructions below as a guide.
        </p>

        <h2>Workflow</h2>
        <p>
            These are the calls that must be made before setting up and processing authentication.            
        </p>
        <Table
            headers={['step #', 'API', 'Additional Mandatory Fields']}
            body={[
                ["1", <SpecLink uriFragment="#operation/TxnAuthkey_CreateAuthkey">Create authkey</SpecLink>, ""],
                ["2", <SpecLink uriFragment="#operation/TxnAuthkey_AttachTxnDetails">Attach transaction details</SpecLink>, <div><b>emailAddress</b> is required</div>],
                ["3", <SpecLink uriFragment="#operation/TxnAuthkey_AttachPaymentMethod">Apply a payment method</SpecLink>, <div>Depending on your integration method of choice, <b>Cardholder name</b> is required</div>],
            ]}        
        />

        <h2>Integrate authentication</h2>
        <p>
            Follow these steps to integrate 3D Secure authentication.
        </p>
        <h3>Step 1 - Initiate authentication</h3>
        <p>
            Make a call to <SpecLink uriFragment="#operation/TxnAuthkey_SetupAuthenticationIframe">Setup authentication iframe</SpecLink>, passing in the
            AuthKey created at the start of the transaction.
        </p>
        <p>
            The API call will return the following object:
        </p>

        <CodeSamples samples={[
            {
                language: "JSON",
                content: 
`
{
    "iframe": {
      "url": "string",
      "origin": "string"
    },
    "authentication": {
      "status": "string"
    }
}
`
            }
        ]} />

       <p>
        <code>authentication.status</code> has two states.
       </p>
       <ul>
            <li><code>Complete</code>: You may proceed to process the transaction.  There is no challenge for the customer, so <code>iframe</code> can be ignored.</li>
            <li><code>InProgress</code>: You will need to embed an iframe within your page and add an event listener for iframe messages.  The <code>iframe</code> object contains details for the challenge url.</li>
       </ul>

       <p>
        <code>iframe</code> contains details for presenting the challenge.        
       </p>
       <ul>
            <li><code>url</code>: The url contains the challenge that will be loaded into the iFrame presented to the customer.</li>
            <li><code>origin</code>: A message event listener will show, hide and process your payment if <code>event.origin</code> matches <code>origin</code>.  See JavaScript snippet below.</li>
       </ul>

       <h3>Step 2 - Add Challenge iFrame to payment page</h3>
       <p>Add iFrame to your webpage.</p>
       <CodeSamples samples={[
            {
                language: "html",
                content: `<iframe id="authIframe" src="{Url}" style="width: 100%; visibility: hidden; height: 1px;"></iframe>`
            }
       ]} copyButton />
       <p>
            Replace HTML <code>&#123;Url&#125;</code> with <code>url</code> returned from call to <SpecLink uriFragment="#operation/TxnAuthkey_SetupAuthenticationIframe">Setup to Authentication</SpecLink>.
       </p>

       <h3>Step 3 - Add JavaScript to process iFrame</h3>
       <p>
            Add the JavaScript snippet below to process the payment, handle errors, show and hide the iFrame.
       </p>
       <CodeSamples samples={[
            {
                language: "javascript",
                content: 
`
window.addEventListener("message", receiveMessage, false);
function receiveMessage(event) {
    if (event.origin !== "{Origin}")
        return;

    var authIframe = document.getElementById("authIframe");
    if (event.source !== authIframe.contentWindow)
        return;

    if (event.data.type === "AuthenticationComplete") {
        // continue with process call
    } else if (event.data.type === "AuthenticationInternalError") {
        // handle error
    } else if (event.data.type === "AuthenticationUserInteractionRequired") {
        authIframe.setAttribute("style", "width: 100%; visibility: visible; height: 450px;")
    } else if (event.data.type === "AuthenticationUserInteractionFinished") {
        authIframe.setAttribute("style", "width: 100%; visibility: hidden; height: 1px;")
    }
}
`
            }
        ]} copyButton />

        <p>
            Replace JavaScript <code>&#123;Origin&#125;</code> with <code>origin</code> supplied in the response of the setup authentication iframe call.
        </p>

        <p>
            Initially setup the iframe as invisible and listen for the events:
        </p>
        <ul>
            <li><code>AuthenticationUserInteractionRequired</code>: Change the iframe to be visible.</li>
            <li><code>AuthenticationUserInteractionFinished</code>: Change the iframe to be invisible again.</li>
        </ul>
        <p>
            <i>Note: you must not use <code>‘display: none’</code>, nor set a zero width or height, as this will prevent the iframe from functioning.</i>
        </p>
        <p>
            Add functionality to process payment and handle error events:
        </p>
        <ul>
            <li><code>AuthenticationComplete</code>: You can process transaction <SpecLink uriFragment="#operation/TxnAuthkey_Process">Process transaction</SpecLink>.</li>
            <li><code>AuthenticationInternalError</code>: Handle errors.</li>
        </ul>

        <h3>Step 4 - Test card numbers</h3>
        <p>
            To obtain test card numbers to test 3D Secure, please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 
            (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST).
        </p>
    </Page>
}

export default TxnAuthenticationIframePage;